import type { FacetFilter } from "@/features/Facets/types";

/** Campos do formulário de envio que o Magic Fill preenche (PBI 87080). */
export interface DocumentFormFields {
  name: string;
  description: string;
  facets: FacetFilter[];
  // PBI 79328 — atributos de negócio do documento; vão como `metadata` no upload.
  metadata: Record<string, string>;
}

/**
 * Aplica as sugestões do Magic Fill aos campos do formulário.
 *
 * PBI 79328 — o LLM devolve os atributos de negócio aninhados (`metadata`) ou em
 * chaves flat (`metadata.source`); os dois caem em `metadata`. PBI 79319 —
 * facetas sugeridas se somam às já escolhidas; lista vazia ("sem confiança")
 * não apaga nada.
 */
export function applyMagicFillSuggestions<T extends DocumentFormFields>(
  current: T,
  suggested: Record<string, unknown>,
): T {
  const next: T = { ...current, metadata: { ...current.metadata } };
  const writeMetaEntry = (k: string, v: unknown) => {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      next.metadata[k] = String(v);
    } else if (Array.isArray(v)) {
      next.metadata[k] = v.map(String).join(", ");
    }
  };

  for (const [key, value] of Object.entries(suggested)) {
    if (key === "name") {
      next.name = String(value ?? "");
    } else if (key === "description") {
      next.description = String(value ?? "");
    } else if (key === "metadata" && value && typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value as Record<string, unknown>).forEach(([k, v]) => writeMetaEntry(k, v));
    } else if (key.startsWith("metadata.")) {
      writeMetaEntry(key.slice("metadata.".length), value);
    } else if (key === "facets" && Array.isArray(value) && value.length > 0) {
      const seen = new Set(current.facets.map((f) => `${f.name}:${f.value}`));
      const merged = [...current.facets];
      for (const item of value as FacetFilter[]) {
        const k = `${item.name}:${item.value}`;
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(item);
        }
      }
      next.facets = merged;
    }
  }
  return next;
}

/**
 * Monta o `metadata` gravado em `storage.metadata`: descarta vazios (sem
 * `{source:""}`), transforma tags separadas por vírgula em array e põe as
 * facetas em `metadata.facets` — o mesmo shape do workflow save_in_storage.
 */
export function buildDocumentMetadata(
  fields: Pick<DocumentFormFields, "facets" | "metadata">,
): Record<string, unknown> {
  const documentMetadata: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields.metadata)) {
    if (typeof v !== "string" || v.trim().length === 0) continue;
    documentMetadata[k] =
      k === "tags" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v.trim();
  }
  documentMetadata.facets = fields.facets.map((f) => ({
    name: String(f.name),
    value: String(f.value),
  }));
  return documentMetadata;
}
